/**
 * Find a payment (#1812): type a check number (or the amount) and see the
 * payment with every invoice it paid — invoice #, invoice date, what this
 * payment put on it, the invoice total, and what it still owes.
 *
 * Used on the customer page's Invoices tab (scoped to that customer) and on
 * Money → Payments (every customer). Built to threads/payment-lookup/mockup.html.
 *
 * Checks typed only into QuickBooks never reach Groundwork (QBO sync is
 * push-only), so "nothing found" says where else to look instead of implying
 * the check doesn't exist.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Printer, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDateOnly, formatDollars } from "@/lib/utils";
import { formatPaidDate } from "@/lib/paid-date";

export interface PaymentLookupInvoice {
    invoice_id: string;
    invoice_number: string | null;
    invoice_date: string | null;
    applied: number;
    invoice_total: number;
    balance: number;
    status: string;
}

export interface PaymentLookupGroup {
    key: string;
    paid_at: string;
    method: string;
    reference: string | null;
    note: string | null;
    customer_id: string | null;
    customer_name: string | null;
    total: number;
    invoices: PaymentLookupInvoice[];
}

interface LookupResponse {
    query: { term: string | null; numeric: boolean; amount: number | null } | null;
    payments: PaymentLookupGroup[];
    truncated: boolean;
}

const METHOD_LABEL: Record<string, string> = {
    check: "Check",
    cash: "Cash",
    ach: "ACH",
    stripe: "Card",
    card: "Card",
    credit_memo: "Credit",
    other: "Payment",
};

export function methodLabel(method: string): string {
    return METHOD_LABEL[method.toLowerCase()] ?? method.charAt(0).toUpperCase() + method.slice(1);
}

/** "Check #1845" when the reference is a check number; ST/Stripe ids are not shown as one. */
export function paymentTitle(p: Pick<PaymentLookupGroup, "method" | "reference" | "note">): string {
    const ref = (p.reference ?? "").trim();
    const machine = /^(st:|pi_|ch_|py_|cs_)/i.test(ref);
    const label = methodLabel(p.method);
    if (ref && !machine) return `${label} #${ref.replace(/^(?:check|chk|ck)\.?\s*/i, "").replace(/^#\s*/, "")}`;
    const memoCheck = /(?:check|chk|ck)\.?\s*#?\s*(\d{2,})/i.exec(p.note ?? "");
    if (memoCheck) return `${label} #${memoCheck[1]}`;
    return label;
}

/** The plain report Regina asked for — one payment, the invoices it paid. */
function printPayment(p: PaymentLookupGroup) {
    const w = window.open("", "_blank", "width=820,height=640");
    if (!w) return;
    const esc = (s: string) =>
        s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
    const rows = p.invoices
        .map(
            (i) =>
                `<tr><td>${esc(i.invoice_number ?? "—")}</td><td>${i.invoice_date ? esc(formatDateOnly(i.invoice_date)) : "—"}</td>` +
                `<td class="n">${esc(formatDollars(i.applied))}</td><td class="n">${esc(formatDollars(i.invoice_total))}</td>` +
                `<td class="n">${i.balance > 0.005 ? esc(formatDollars(i.balance)) : "Paid in full"}</td></tr>`,
        )
        .join("");
    w.document.write(
        `<!doctype html><html><head><meta charset="utf-8"><title>${esc(paymentTitle(p))}</title>` +
            `<style>body{font:14px system-ui,sans-serif;margin:32px;color:#1c1917}h1{font-size:20px;margin:0 0 4px}` +
            `p{margin:0 0 16px;color:#57534e}table{border-collapse:collapse;width:100%}th,td{border-bottom:1px solid #d6d3d1;padding:8px;text-align:left}` +
            `th{font-size:12px;text-transform:uppercase;color:#57534e}.n{text-align:right}tfoot td{font-weight:700;border-bottom:0}</style></head><body>` +
            `<h1>${esc(paymentTitle(p))} — ${esc(formatDollars(p.total))}</h1>` +
            `<p>${esc(p.customer_name ?? "")} · received ${esc(formatPaidDate(p.paid_at))} · paid ${p.invoices.length} invoice${p.invoices.length === 1 ? "" : "s"}</p>` +
            `<table><thead><tr><th>Invoice #</th><th>Invoice date</th><th class="n">Paid by this</th><th class="n">Invoice total</th><th class="n">Still owes</th></tr></thead>` +
            `<tbody>${rows}</tbody><tfoot><tr><td colspan="2">Total</td><td class="n">${esc(formatDollars(p.total))}</td><td></td><td></td></tr></tfoot></table>` +
            `<script>window.onload=()=>window.print()</script></body></html>`,
    );
    w.document.close();
}

function PaymentCard({ p, showCustomer }: { p: PaymentLookupGroup; showCustomer: boolean }) {
    return (
        <div className="rounded-lg border bg-card" data-testid="payment-lookup-result">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b bg-emerald-50 px-3 py-2 dark:bg-emerald-950/40">
                <span className="rounded-md bg-emerald-700 px-2 py-0.5 text-sm font-bold text-white">
                    {paymentTitle(p)}
                </span>
                <span className="text-lg font-black tabular-nums text-foreground">{formatDollars(p.total)}</span>
                <span className="text-sm text-muted-foreground">received {formatPaidDate(p.paid_at)}</span>
                {showCustomer && p.customer_id && (
                    <Link
                        to={`/customers/${p.customer_id}?tab=invoices`}
                        className="text-sm font-medium hover:underline"
                    >
                        {p.customer_name}
                    </Link>
                )}
                <span className="text-sm text-muted-foreground">
                    paid {p.invoices.length} invoice{p.invoices.length === 1 ? "" : "s"}
                </span>
                <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto h-7"
                    onClick={() => printPayment(p)}
                    data-testid="payment-lookup-print"
                >
                    <Printer className="mr-1 h-3.5 w-3.5" /> Print
                </Button>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase text-muted-foreground">
                            <th className="px-3 py-1.5 font-semibold">Invoice #</th>
                            <th className="px-3 py-1.5 font-semibold">Invoice date</th>
                            <th className="px-3 py-1.5 text-right font-semibold">Paid by this</th>
                            <th className="px-3 py-1.5 text-right font-semibold">Invoice total</th>
                            <th className="px-3 py-1.5 text-right font-semibold">Still owes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {p.invoices.map((i) => (
                            <tr key={i.invoice_id} className="border-t">
                                <td className="px-3 py-1.5 font-mono font-semibold">
                                    <Link to={`/invoices/${i.invoice_id}`} className="text-primary hover:underline">
                                        {i.invoice_number ?? "—"}
                                    </Link>
                                </td>
                                <td className="px-3 py-1.5">{i.invoice_date ? formatDateOnly(i.invoice_date) : "—"}</td>
                                <td className="px-3 py-1.5 text-right font-semibold tabular-nums">
                                    {formatDollars(i.applied)}
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatDollars(i.invoice_total)}
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                    {i.balance > 0.005 ? (
                                        <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold tabular-nums text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                                            {formatDollars(i.balance)}
                                        </span>
                                    ) : (
                                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100">
                                            Paid in full
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {p.note && !/^ServiceTitan payment /.test(p.note) && (
                <div className="border-t px-3 py-1.5 text-xs text-muted-foreground">Memo: {p.note}</div>
            )}
        </div>
    );
}

export function PaymentLookup({ customerId }: { customerId?: string }) {
    const [text, setText] = useState("");
    const [submitted, setSubmitted] = useState("");
    const q = useQuery({
        queryKey: ["payment-lookup", customerId ?? "all", submitted],
        queryFn: () => {
            const params = new URLSearchParams({ q: submitted });
            if (customerId) params.set("customerId", customerId);
            return api.get<LookupResponse>(`/api/money/payment-lookup?${params.toString()}`);
        },
        enabled: submitted.trim().length > 0,
    });
    const payments = q.data?.payments ?? [];

    return (
        <div className="space-y-3" data-testid="payment-lookup">
            <form
                className="flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                    e.preventDefault();
                    setSubmitted(text.trim());
                }}
            >
                <div className="relative min-w-[220px] flex-1 sm:max-w-sm">
                    <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Check # or amount — 1845 or $3,782.16"
                        aria-label="Find a payment by check number or amount"
                        className="pl-8"
                        data-testid="payment-lookup-input"
                    />
                </div>
                <Button type="submit" size="sm" disabled={!text.trim()} data-testid="payment-lookup-find">
                    Find payment
                </Button>
            </form>

            {q.isFetching && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Looking…
                </p>
            )}
            {q.isError && (
                <p className="text-sm text-red-700 dark:text-red-300">Couldn't search payments. Try again.</p>
            )}
            {!q.isFetching && q.isSuccess && submitted && payments.length === 0 && (
                <div
                    className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
                    data-testid="payment-lookup-empty"
                >
                    <p className="font-semibold">No payment in Groundwork matches “{submitted}”.</p>
                    <p>
                        Payments typed only into QuickBooks don't come into Groundwork — look there. Or try the amount
                        instead, like $3,782.16.
                    </p>
                </div>
            )}
            {payments.map((p) => (
                <PaymentCard key={p.key} p={p} showCustomer={!customerId} />
            ))}
            {q.data?.truncated && (
                <p className="text-xs text-muted-foreground">
                    Showing the newest matches. Type more of the number to narrow it.
                </p>
            )}
        </div>
    );
}
